import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Zap, 
  Shield, 
  CheckCircle, 
  XCircle, 
  ArrowRight, 
  Settings,
  Youtube,
  Music2,
  Instagram,
  Facebook,
  Clock,
  Target
} from 'lucide-react';

const API_URL = window.__BACKEND_URL;

/**
 * Autopilot Eligibility Page
 * 
 * Shows:
 * - Autopilot status (Enabled/Disabled)
 * - CTA to enable in Account Settings if disabled
 * - Eligibility checklist
 */
export default function AutopilotCreate() {
  const navigate = useNavigate();
  const [autopilotEnabled, setAutopilotEnabled] = useState(false);
  const [userPlan, setUserPlan] = useState('free');
  const [loading, setLoading] = useState(true);
  const [platformStatus, setPlatformStatus] = useState(null);

  useEffect(() => {
    checkAutopilotStatus();
    fetchPlatformStatus();
  }, []);

  const checkAutopilotStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/account/settings/autopilot`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setAutopilotEnabled(data.autopilot_enabled || false);
        setUserPlan(data.plan_tier || 'free');
      }
    } catch (err) {
      console.error('Failed to check autopilot status:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPlatformStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch platform status:', err);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl" data-testid="autopilot-eligibility-page">
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const connectedCount = platformStatus?.connected_count || 0;
  const hasProPlan = ['pro', 'agency', 'agency_elite'].includes(userPlan);

  // Eligibility requirements
  const eligibilityChecklist = [
    {
      id: 'autopilot_enabled',
      label: 'Autopilot Settings Enabled',
      description: 'Enable autopilot in your account settings',
      met: autopilotEnabled,
      action: () => navigate('/app/account/settings')
    },
    {
      id: 'platform_connected',
      label: 'At Least 1 Platform Connected',
      description: 'Connect YouTube, TikTok, Instagram, or Facebook',
      met: connectedCount >= 1,
      action: () => navigate('/app/account/creator-profile')
    },
    {
      id: 'content_ready',
      label: 'Content Ready for Posting',
      description: 'Have approved clips ready in your content queue',
      met: false, // Placeholder - would need to check content queue
      action: () => navigate('/app/create/content-studio')
    }
  ];

  const allRequirementsMet = eligibilityChecklist.every(req => req.met);

  return (
    <div className="container mx-auto p-6 max-w-7xl" data-testid="autopilot-eligibility-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Zap className="h-8 w-8 text-purple-500" />
          Autopilot Eligibility
        </h1>
        <p className="text-[#8A93A6]">Check your eligibility for automated content posting</p>
      </div>

      {/* Status Card */}
      <Card className="glass mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-500" />
              Autopilot Status
            </CardTitle>
            <Badge 
              variant="outline" 
              className={autopilotEnabled ? 'border-green-500/30 text-green-400' : 'border-red-500/30 text-red-400'}
            >
              {autopilotEnabled ? (
                <><CheckCircle className="h-3 w-3 mr-1" />Enabled</>
              ) : (
                <><XCircle className="h-3 w-3 mr-1" />Disabled</>
              )}
            </Badge>
          </div>
          <CardDescription>
            {autopilotEnabled 
              ? 'Autopilot is active and ready to post your content automatically'
              : 'Enable autopilot to automatically schedule and post your content'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {autopilotEnabled ? (
            <Alert className="bg-green-500/10 border-green-500/30">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <AlertDescription className="text-green-400">
                Autopilot is enabled. Content will be scheduled and posted automatically after your approval.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              <Alert className="bg-yellow-500/10 border-yellow-500/30">
                <Shield className="h-4 w-4 text-yellow-500" />
                <AlertDescription className="text-yellow-400">
                  Autopilot is currently disabled. Enable it in your account settings to use automated posting.
                </AlertDescription>
              </Alert>
              <Button 
                onClick={() => navigate('/app/account/settings')}
                className="w-full bg-purple-500 hover:bg-purple-600"
                data-testid="enable-autopilot-btn"
              >
                <Settings className="h-4 w-4 mr-2" />
                Go to Account Settings → Autopilot Settings
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Eligibility Checklist */}
      <Card className="glass mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-cyan-500" />
              Eligibility Checklist
            </CardTitle>
            <Badge 
              variant="outline" 
              className={allRequirementsMet ? 'border-green-500/30 text-green-400' : 'border-yellow-500/30 text-yellow-400'}
            >
              {eligibilityChecklist.filter(r => r.met).length}/{eligibilityChecklist.length} Complete
            </Badge>
          </div>
          <CardDescription>Complete these requirements to use Autopilot</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {eligibilityChecklist.map((req) => (
              <div 
                key={req.id}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  req.met 
                    ? 'bg-green-500/5 border-green-500/20' 
                    : 'bg-black/40 border-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  {req.met ? (
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  )}
                  <div>
                    <p className={`font-medium ${req.met ? 'text-white' : 'text-[#8A93A6]'}`}>
                      {req.label}
                    </p>
                    <p className="text-xs text-[#8A93A6] mt-0.5">{req.description}</p>
                  </div>
                </div>
                {!req.met && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={req.action}
                    className="border-white/10 text-xs"
                  >
                    Fix
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Connected Platforms Overview */}
      <Card className="glass mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-500" />
            Connected Platforms
          </CardTitle>
          <CardDescription>Platforms available for autopilot posting</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { name: 'YouTube', icon: Youtube, color: 'text-red-500', bgColor: 'bg-red-500/10', key: 'youtube' },
              { name: 'TikTok', icon: Music2, color: 'text-pink-500', bgColor: 'bg-pink-500/10', key: 'tiktok' },
              { name: 'Instagram', icon: Instagram, color: 'text-purple-500', bgColor: 'bg-purple-500/10', key: 'instagram' },
              { name: 'Facebook', icon: Facebook, color: 'text-blue-500', bgColor: 'bg-blue-500/10', key: 'facebook' }
            ].map((platform) => {
              const isConnected = platformStatus?.connections?.[platform.key]?.is_connected;
              return (
                <div 
                  key={platform.key}
                  className={`p-3 rounded-lg border text-center ${
                    isConnected 
                      ? `${platform.bgColor} border-${platform.color.replace('text-', '')}/30` 
                      : 'bg-black/40 border-white/10 opacity-50'
                  }`}
                >
                  <platform.icon className={`h-6 w-6 mx-auto mb-2 ${isConnected ? platform.color : 'text-gray-500'}`} />
                  <p className={`text-xs font-medium ${isConnected ? 'text-white' : 'text-gray-500'}`}>
                    {platform.name}
                  </p>
                  <p className={`text-xs mt-1 ${isConnected ? 'text-green-400' : 'text-gray-500'}`}>
                    {isConnected ? 'Connected' : 'Not connected'}
                  </p>
                </div>
              );
            })}
          </div>
          
          {connectedCount === 0 && (
            <Button 
              variant="outline" 
              onClick={() => navigate('/app/account/creator-profile')}
              className="w-full mt-4 border-white/10"
            >
              Connect a Platform
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Autopilot Features (when eligible) */}
      {allRequirementsMet && (
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-purple-500" />
              You're Ready for Autopilot!
            </CardTitle>
            <CardDescription>Start automating your content posting</CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="bg-purple-500/10 border-purple-500/30 mb-4">
              <Zap className="h-4 w-4 text-purple-500" />
              <AlertDescription className="text-purple-400">
                All eligibility requirements met. Autopilot will automatically schedule and post your approved content.
              </AlertDescription>
            </Alert>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-black/40 border border-white/10">
                <p className="text-white font-medium mb-1">Auto-Scheduling</p>
                <p className="text-xs text-[#8A93A6]">Content is scheduled at optimal times for each platform</p>
              </div>
              <div className="p-4 rounded-lg bg-black/40 border border-white/10">
                <p className="text-white font-medium mb-1">Approval-First</p>
                <p className="text-xs text-[#8A93A6]">Nothing posts without your explicit approval</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
